import { Download, EllipsisVertical, GanttChart, GripVertical, Pause, Play, RotateCcw, X } from "lucide-react";
import { useState } from "react";
import type { AnimationNodeKind, AnimationGraph } from "@/document/model/animation";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { supportedAnimationExportFormats, type AnimationExportFormat } from "@/export/animationExporter";
import { shouldBlurAfterPointerScrub } from "./animationFocus";
import { AnimationTimeline, type AnimationLoopRegion, type AnimationTimelineViewport } from "./AnimationTimeline";

export const ANIMATION_TIMELINE_HEIGHT = 160;
export const ANIMATION_PANEL_CONTROLS_HEIGHT = 40;

interface AnimationPanelProps {
  open: boolean;
  playing: boolean;
  timeMs: number;
  durationMs: number;
  timelineOpen: boolean;
  loopRegion?: AnimationLoopRegion;
  hasActiveAnimation: boolean;
  animationGraph: AnimationGraph;
  activeAnimationRootId?: string;
  selectedAnimationNodeIds: string[];
  primarySelectedAnimationNodeId: string | undefined;
  armedNodeKind?: AnimationNodeKind;
  timelineViewport: AnimationTimelineViewport;
  onOpenChange: (open: boolean) => void;
  onArmNodeKind: (kind: AnimationNodeKind | undefined) => void;
  onPlay: () => void;
  onPause: () => void;
  onReset: () => void;
  onTimelineOpenChange: (open: boolean) => void;
  onTimeChange: (timeMs: number) => void;
  onLoopRegionChange: (region: AnimationLoopRegion | undefined) => void;
  onSelectAnimationNodes: (ids: string[], primaryId: string | undefined, options?: { switchRoot?: boolean }) => void;
  onUpdateMotionNode: (nodeId: string, changes: { durationMs?: number; offsetMs?: number; name?: string }) => void;
  onExport: (format: AnimationExportFormat) => void;
  onCopyMermaid: () => void;
  onBake: () => void;
  exportingFormat?: AnimationExportFormat;
  onTimelineViewportChange: (viewport: AnimationTimelineViewport) => void;
}

const palette: Array<{ kind: AnimationNodeKind; label: string }> = [
  { kind: "animation", label: "Animation" },
  { kind: "group", label: "Group" },
  { kind: "animate", label: "Behavior" },
  { kind: "motion", label: "Motion" },
];

export function AnimationPanel({
  open,
  playing,
  timeMs,
  durationMs,
  timelineOpen,
  loopRegion,
  hasActiveAnimation,
  animationGraph,
  activeAnimationRootId,
  selectedAnimationNodeIds,
  primarySelectedAnimationNodeId: _primarySelectedAnimationNodeId,
  armedNodeKind,
  timelineViewport,
  onOpenChange,
  onArmNodeKind,
  onPlay,
  onPause,
  onReset,
  onTimelineOpenChange,
  onTimeChange,
  onLoopRegionChange,
  onSelectAnimationNodes,
  onUpdateMotionNode,
  onExport,
  onCopyMermaid,
  onBake,
  exportingFormat,
  onTimelineViewportChange,
}: AnimationPanelProps) {
  const document = useStoreSubscribe(documentStore.document);
  const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  function handleTimeRangePointerEnd(event: React.PointerEvent<HTMLInputElement>): void {
    if (shouldBlurAfterPointerScrub(event.pointerType)) {
      event.currentTarget.blur();
    }
  }

  if (!open) {
    return null;
  }

  const controlsEnabled = hasActiveAnimation;
  const exportDisabled = !controlsEnabled || Boolean(exportingFormat);

  return (
    <div className="pointer-events-auto border-t border-white/10 bg-[#151a21]/95 text-slate-200 backdrop-blur">
      {/* Visual timeline */}
      {timelineOpen && (
        <div style={{ height: ANIMATION_TIMELINE_HEIGHT }} className="border-b border-white/10">
          <AnimationTimeline
            animationGraph={animationGraph}
            document={document}
            rootNodeId={activeAnimationRootId}
            timeMs={timeMs}
            durationMs={durationMs}
            playing={playing}
            selectedAnimationNodeIds={selectedAnimationNodeIds}
            loopRegion={loopRegion}
            viewport={timelineViewport}
            onTimeChange={t => onTimeChange(Math.max(0, Math.min(t, durationMs)))}
            onSelectAnimationNodes={onSelectAnimationNodes}
            onUpdateMotionNode={onUpdateMotionNode}
            onLoopRegionChange={onLoopRegionChange}
            onViewportChange={onTimelineViewportChange}
          />
        </div>
      )}

      {/* Controls bar */}
      <div style={{ height: ANIMATION_PANEL_CONTROLS_HEIGHT }} className="flex items-center gap-2 px-3">
        {/* Timeline toggle button */}
        <button
          type="button"
          className={`grid size-7 place-items-center rounded border transition-colors ${
            timelineOpen
              ? "border-sky-400/60 bg-sky-400/10 text-sky-300"
              : "border-white/10 bg-white/[0.04] text-slate-400 hover:text-slate-200"
          }`}
          onClick={() => onTimelineOpenChange(!timelineOpen)}
          aria-label={timelineOpen ? "Hide timeline" : "Show timeline"}
          title={timelineOpen ? "Hide timeline" : "Show timeline"}
        >
          <GanttChart className="size-3.5" aria-hidden="true" />
        </button>

        <div className="mx-1 h-5 w-px bg-white/10" />

        {/* Node palette */}
        <div className="flex items-center gap-1">
          {palette.map(item => (
            <button
              key={item.kind}
              type="button"
              draggable
              className={`flex h-7 cursor-grab items-center gap-0.5 rounded border pl-1 pr-2 text-[12px] ${
                armedNodeKind === item.kind
                  ? "border-sky-400/70 bg-sky-400/10 text-sky-100"
                  : "border-white/10 bg-[#0f1318] hover:border-sky-400/60 hover:text-sky-100"
              }`}
              onClick={() => onArmNodeKind(armedNodeKind === item.kind ? undefined : item.kind)}
              onDragStart={event => {
                event.dataTransfer.setData("application/x-curva-animation-node", item.kind);
                event.dataTransfer.effectAllowed = "copy";
                onArmNodeKind(undefined);
              }}
            >
              <GripVertical className="size-3 shrink-0 text-slate-600" aria-hidden="true" />
              {item.label}
            </button>
          ))}
        </div>

        <div className="mx-1 h-5 w-px bg-white/10" />

        <button
          type="button"
          className="grid size-7 place-items-center rounded border border-white/10 bg-white/[0.04] disabled:opacity-40"
          disabled={!controlsEnabled}
          onClick={playing ? onPause : onPlay}
          aria-label={playing ? "Pause animation" : "Play animation"}
          title={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="size-3.5" aria-hidden="true" /> : <Play className="size-3.5" aria-hidden="true" />}
        </button>
        <button
          type="button"
          className="grid size-7 place-items-center rounded border border-white/10 bg-white/[0.04] disabled:opacity-40"
          disabled={!controlsEnabled}
          onClick={onReset}
          aria-label="Reset animation"
          title="Reset"
        >
          <RotateCcw className="size-3.5" aria-hidden="true" />
        </button>
        <input
          type="range"
          min={0}
          max={Math.max(1, durationMs)}
          value={Math.min(timeMs, Math.max(1, durationMs))}
          disabled={!controlsEnabled}
          onChange={event => onTimeChange(Number(event.currentTarget.value))}
          onPointerUp={handleTimeRangePointerEnd}
          onPointerCancel={handleTimeRangePointerEnd}
          className="h-1 min-w-48 flex-1 accent-sky-400 disabled:opacity-40"
        />
        <div className="w-24 text-right text-[11px] tabular-nums text-slate-400">{Math.round(timeMs)} / {Math.round(durationMs)}ms</div>
        <div className="mx-1 h-5 w-px bg-white/10" />
        <div className="relative">
          <button
            type="button"
            className="grid size-7 place-items-center rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40"
            disabled={!controlsEnabled}
            onClick={() => {
              setExportMenuOpen(false);
              setActionsMenuOpen(open => !open);
            }}
            aria-label="Animation actions"
            title="Animation actions"
            aria-haspopup="menu"
            aria-expanded={actionsMenuOpen}
          >
            <EllipsisVertical className="size-3.5" aria-hidden="true" />
          </button>
          {actionsMenuOpen ? (
            <div
              className="absolute bottom-9 right-0 z-50 w-56 rounded border border-white/10 bg-[#11161d] py-1 text-[12px] shadow-xl"
              role="menu"
            >
              <button
                type="button"
                className="h-7 w-full px-2 text-left text-slate-200 hover:bg-white/[0.08]"
                onClick={() => {
                  setActionsMenuOpen(false);
                  onBake();
                }}
                role="menuitem"
              >
                Bake animation into current frame
              </button>
            </div>
          ) : null}
        </div>
        <div className="relative">
          <button
            type="button"
            className="grid size-7 place-items-center rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] disabled:opacity-40"
            disabled={exportDisabled}
            onClick={() => {
              setActionsMenuOpen(false);
              setExportMenuOpen(open => !open);
            }}
            aria-label="Export animation"
            title={exportingFormat ? `Exporting ${exportingFormat.toUpperCase()}` : "Export animation"}
          >
            <Download className="size-3.5" aria-hidden="true" />
          </button>
          {exportMenuOpen ? (
            <div
              className="absolute bottom-9 right-0 z-50 w-36 rounded border border-white/10 bg-[#11161d] py-1 text-[12px] shadow-xl"
              role="menu"
            >
              {supportedAnimationExportFormats().map(format => (
                <button
                  key={format}
                  type="button"
                  className="h-7 w-full px-2 text-left text-slate-200 hover:bg-white/[0.08]"
                  onClick={() => {
                    setExportMenuOpen(false);
                    onExport(format);
                  }}
                  role="menuitem"
                >
                  Export to {format}
                </button>
              ))}
              <button
                type="button"
                className="h-7 w-full px-2 text-left text-slate-200 hover:bg-white/[0.08]"
                onClick={() => {
                  setExportMenuOpen(false);
                  onCopyMermaid();
                }}
                role="menuitem"
              >
                Copy as Mermaid
              </button>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          className="grid size-7 place-items-center rounded border border-white/10 bg-white/[0.04] hover:bg-white/[0.08]"
          onClick={() => onOpenChange(false)}
          aria-label="Close animation panel"
          title="Close animation panel"
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
